/*
 * @Author: Ryan
 * @Date: 2026-01-08
 * @Description: Landing 页面通用头部组件
 */
import { DownOutlined, MenuOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import Link from 'next/link';
import { useContext, useMemo, useState } from 'react';

import { RightContext } from '@/components/RightProvider/context';
import { HOME_CTA_URL } from '@/landing/e-commerce/new-home/config';
import {
  NEW_HOME_FEATURE_GROUPS,
  NEW_HOME_USE_CASE_LINKS,
} from '@/landing/e-commerce/new-home/header-nav';
import { pushGTMEvent } from '@/utils/eventlog/gtmDataLayer';
import { pricingURL, viralTemplateURL } from '@/utils/routeConstants';

import styles from './index.module.scss';

interface LandingHeaderProps {
  transparent?: boolean;
}

const LandingHeader: React.FC<LandingHeaderProps> = () => {
  const rightContext = useContext(RightContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<'features' | 'useCases' | null>(
    null,
  );

  const featureMenuItems = useMemo<MenuProps['items']>(
    () =>
      NEW_HOME_FEATURE_GROUPS.map((group) => ({
        type: 'group' as const,
        label: group.label,
        children: group.links.map((item) => ({
          key: item.key,
          label: (
            <Link href={item.href} className={styles.navDropdownLink}>
              {item.label}
            </Link>
          ),
        })),
      })),
    [],
  );

  const useCaseMenuItems = useMemo<MenuProps['items']>(
    () =>
      NEW_HOME_USE_CASE_LINKS.map((item) => ({
        key: item.key,
        label: (
          <Link href={item.href} className={styles.navDropdownLink}>
            {item.label}
          </Link>
        ),
      })),
    [],
  );

  const handleLogin = async () => {
    pushGTMEvent('login_popup', {
      trigger_type: 'nav_menu',
      nav_menu_item: 'login_btn',
    });
    await rightContext.getUserLoginState({
      skipGTMTracking: true,
    });
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setExpandedMobile(null);
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        <Link href="/" className={styles.logoLink}>
          <FalcoCutLogo />
        </Link>

        <nav className={styles.nav}>
          <Dropdown
            menu={{ items: featureMenuItems, className: styles.navDropdownMenu }}
            trigger={['hover']}
            placement="bottomLeft"
            overlayClassName={styles.navDropdown}
          >
            <button type="button" className={styles.navDropdownTrigger}>
              Features
              <DownOutlined />
            </button>
          </Dropdown>
          <Link href={viralTemplateURL}>Templates</Link>
          <Dropdown
            menu={{ items: useCaseMenuItems, className: styles.navDropdownMenu }}
            trigger={['hover']}
            placement="bottomLeft"
            overlayClassName={styles.navDropdown}
          >
            <button type="button" className={styles.navDropdownTrigger}>
              Use Cases
              <DownOutlined />
            </button>
          </Dropdown>
          <Link href={pricingURL}>Pricing</Link>
        </nav>

        <div className={styles.headerActions}>
          <button type="button" className={styles.loginButton} onClick={handleLogin}>
            Log in
          </button>
          <Link href={HOME_CTA_URL} className={styles.headerCta}>
            Start Creating
          </Link>
          <button
            type="button"
            className={styles.mobileButton}
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            <MenuOutlined />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className={styles.mobileMenu}>
          <div className={styles.mobileMenuBlock}>
            <button
              type="button"
              className={styles.mobileMenuRow}
              onClick={() =>
                setExpandedMobile((prev) => (prev === 'features' ? null : 'features'))
              }
            >
              Features
              <span>{expandedMobile === 'features' ? '-' : '+'}</span>
            </button>
            {expandedMobile === 'features' &&
              NEW_HOME_FEATURE_GROUPS.map((group) => (
                <div key={group.key} className={styles.mobileMenuGroup}>
                  <div className={styles.mobileMenuGroupTitle}>{group.label}</div>
                  {group.links.map((link) => (
                    <Link
                      key={link.key}
                      href={link.href}
                      className={styles.mobileMenuLink}
                      onClick={closeMobile}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              ))}
          </div>

          <Link
            href={viralTemplateURL}
            className={styles.mobileMenuRowLink}
            onClick={closeMobile}
          >
            Templates
          </Link>

          <div className={styles.mobileMenuBlock}>
            <button
              type="button"
              className={styles.mobileMenuRow}
              onClick={() =>
                setExpandedMobile((prev) => (prev === 'useCases' ? null : 'useCases'))
              }
            >
              Use Cases
              <span>{expandedMobile === 'useCases' ? '-' : '+'}</span>
            </button>
            {expandedMobile === 'useCases' &&
              NEW_HOME_USE_CASE_LINKS.map((link) => (
                <Link
                  key={link.key}
                  href={link.href}
                  className={styles.mobileMenuLink}
                  onClick={closeMobile}
                >
                  {link.label}
                </Link>
              ))}
          </div>

          <Link
            href={pricingURL}
            className={styles.mobileMenuRowLink}
            onClick={closeMobile}
          >
            Pricing
          </Link>
        </div>
      )}
    </header>
  );
};

const FalcoCutLogo = () => (
  <>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M9.32608 9.17214C9.32608 6.3867 11.5841 4.12866 14.3696 4.12866H29.5C29.5 6.9141 27.242 9.17214 24.4565 9.17214H9.32608Z"
        fill="url(#landing_header_logo_icon_a)"
      />
      <path
        d="M0.5 16.1069C0.5 13.3215 2.75804 11.0634 5.54348 11.0634H20.6739C20.6739 13.8489 18.4159 16.1069 15.6304 16.1069H0.5Z"
        fill="url(#landing_header_logo_icon_b)"
      />
      <path
        d="M11.2978 21.9884C10.6563 24.699 7.93904 26.3761 5.2285 25.7345L7.05956 17.9982H12.2422L11.2978 21.9884Z"
        fill="url(#landing_header_logo_icon_c)"
      />
      <defs>
        <linearGradient
          id="landing_header_logo_icon_a"
          x1="6.61392"
          y1="25.5173"
          x2="24.3434"
          y2="11.273"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#8F49FF" />
          <stop offset="1" stopColor="#3CC2FF" />
        </linearGradient>
        <linearGradient
          id="landing_header_logo_icon_b"
          x1="6.61392"
          y1="25.5173"
          x2="24.3434"
          y2="11.273"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#8F49FF" />
          <stop offset="1" stopColor="#3CC2FF" />
        </linearGradient>
        <linearGradient
          id="landing_header_logo_icon_c"
          x1="6.61392"
          y1="25.5173"
          x2="24.3434"
          y2="11.273"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#8F49FF" />
          <stop offset="1" stopColor="#3CC2FF" />
        </linearGradient>
      </defs>
    </svg>
    <svg
      width="80"
      height="15"
      viewBox="0 0 80 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="FalcoCut"
      role="img"
    >
      <path
        d="M5.86808e-05 14.46V0.399961H2.72006V14.46H5.86808e-05ZM2.00006 8.69996V6.27996H9.08006V8.69996H2.00006ZM2.00006 2.79996V0.399961H9.42006V2.79996H2.00006ZM15.0905 14.66C14.2105 14.66 13.4172 14.44 12.7105 14C12.0039 13.56 11.4505 12.96 11.0505 12.2C10.6505 11.44 10.4505 10.5866 10.4505 9.63996C10.4505 8.67996 10.6505 7.81996 11.0505 7.05996C11.4505 6.29996 12.0039 5.69996 12.7105 5.25996C13.4172 4.81996 14.2105 4.59996 15.0905 4.59996C15.7839 4.59996 16.4039 4.73996 16.9505 5.01996C17.4972 5.29996 17.9305 5.69329 18.2505 6.19996C18.5839 6.69329 18.7639 7.25329 18.7905 7.87996V11.36C18.7639 12 18.5839 12.5666 18.2505 13.06C17.9305 13.5533 17.4972 13.9466 16.9505 14.24C16.4039 14.52 15.7839 14.66 15.0905 14.66ZM15.5705 12.24C16.3039 12.24 16.8972 12 17.3505 11.52C17.8039 11.0266 18.0305 10.3933 18.0305 9.61996C18.0305 9.11329 17.9239 8.66663 17.7105 8.27996C17.5105 7.87996 17.2239 7.57329 16.8505 7.35996C16.4905 7.13329 16.0639 7.01996 15.5705 7.01996C15.0905 7.01996 14.6639 7.13329 14.2905 7.35996C13.9305 7.57329 13.6439 7.87996 13.4305 8.27996C13.2305 8.66663 13.1305 9.11329 13.1305 9.61996C13.1305 10.14 13.2305 10.6 13.4305 11C13.6439 11.3866 13.9305 11.6933 14.2905 11.92C14.6639 12.1333 15.0905 12.24 15.5705 12.24ZM17.8905 14.46V11.86L18.3105 9.49996L17.8905 7.17996V4.79996H20.4905V14.46H17.8905ZM22.9083 14.46V-4.00543e-05H25.5283V14.46H22.9083ZM32.5097 14.66C31.5231 14.66 30.6364 14.44 29.8497 14C29.0631 13.56 28.4431 12.96 27.9897 12.2C27.5364 11.44 27.3097 10.5866 27.3097 9.63996C27.3097 8.67996 27.5364 7.81996 27.9897 7.05996C28.4431 6.29996 29.0631 5.69996 29.8497 5.25996C30.6497 4.80663 31.5364 4.57996 32.5097 4.57996C33.2697 4.57996 33.9697 4.71996 34.6097 4.99996C35.2497 5.27996 35.8031 5.67996 36.2697 6.19996L34.5897 7.89996C34.3364 7.61996 34.0297 7.40663 33.6697 7.25996C33.3231 7.11329 32.9364 7.03996 32.5097 7.03996C32.0297 7.03996 31.5964 7.15329 31.2097 7.37996C30.8364 7.59329 30.5364 7.89329 30.3097 8.27996C30.0964 8.65329 29.9897 9.09996 29.9897 9.61996C29.9897 10.1266 30.0964 10.58 30.3097 10.98C30.5364 11.3666 30.8364 11.6733 31.2097 11.9C31.5964 12.1133 32.0297 12.22 32.5097 12.22C32.9497 12.22 33.3431 12.1466 33.6897 12C34.0497 11.84 34.3564 11.6133 34.6097 11.32L36.3097 13.02C35.8164 13.5533 35.2497 13.96 34.6097 14.24C33.9697 14.52 33.2697 14.66 32.5097 14.66ZM42.2187 14.66C41.2453 14.66 40.3653 14.44 39.5787 14C38.8053 13.5466 38.1853 12.94 37.7187 12.18C37.2653 11.4066 37.0387 10.5466 37.0387 9.59996C37.0387 8.65329 37.2653 7.80663 37.7187 7.05996C38.172 6.29996 38.792 5.69996 39.5787 5.25996C40.3653 4.80663 41.2387 4.57996 42.1987 4.57996C43.1853 4.57996 44.0653 4.80663 44.8387 5.25996C45.6253 5.69996 46.2453 6.29996 46.6987 7.05996C47.152 7.80663 47.3787 8.65329 47.3787 9.59996C47.3787 10.5466 47.152 11.4066 46.6987 12.18C46.2453 12.94 45.6253 13.5466 44.8387 14C44.0653 14.44 43.192 14.66 42.2187 14.66ZM42.1987 12.22C42.692 12.22 43.1253 12.1133 43.4987 11.9C43.8853 11.6733 44.1787 11.3666 44.3787 10.98C44.592 10.58 44.6987 10.1266 44.6987 9.61996C44.6987 9.11329 44.592 8.66663 44.3787 8.27996C44.1653 7.89329 43.872 7.59329 43.4987 7.37996C43.1253 7.15329 42.692 7.03996 42.1987 7.03996C41.7187 7.03996 41.292 7.15329 40.9187 7.37996C40.5453 7.59329 40.252 7.89329 40.0387 8.27996C39.8253 8.66663 39.7187 9.11329 39.7187 9.61996C39.7187 10.1266 39.8253 10.58 40.0387 10.98C40.252 11.3666 40.5453 11.6733 40.9187 11.9C41.292 12.1133 41.7187 12.22 42.1987 12.22ZM55.9654 14.66C54.9387 14.66 53.9854 14.48 53.1054 14.12C52.2254 13.7466 51.452 13.2333 50.7854 12.58C50.132 11.9133 49.6254 11.14 49.2654 10.26C48.9187 9.37996 48.7454 8.43329 48.7454 7.41996C48.7454 6.40663 48.9187 5.45996 49.2654 4.57996C49.6254 3.69996 50.132 2.93329 50.7854 2.27996C51.452 1.62663 52.2187 1.11329 53.0854 0.73996C53.9654 0.366627 54.9254 0.17996 55.9654 0.17996C57.0854 0.17996 58.072 0.366627 58.9254 0.73996C59.7787 1.09996 60.532 1.59996 61.1854 2.23996L59.3454 4.07996C58.9587 3.65329 58.4854 3.31996 57.9254 3.07996C57.3654 2.83996 56.712 2.71996 55.9654 2.71996C55.312 2.71996 54.712 2.83329 54.1654 3.05996C53.632 3.27329 53.1654 3.59329 52.7654 4.01996C52.3654 4.43329 52.0587 4.93329 51.8454 5.51996C51.632 6.09329 51.5254 6.72663 51.5254 7.41996C51.5254 8.12663 51.632 8.76663 51.8454 9.33996C52.0587 9.91329 52.3654 10.4133 52.7654 10.84C53.1654 11.2533 53.632 11.5733 54.1654 11.8C54.712 12.0266 55.312 12.14 55.9654 12.14C56.752 12.14 57.4254 12.02 57.9854 11.78C58.5587 11.54 59.0387 11.2 59.4254 10.76L61.2654 12.6C60.612 13.2533 59.852 13.76 58.9854 14.12C58.1187 14.48 57.112 14.66 55.9654 14.66ZM67.0963 14.66C66.243 14.66 65.483 14.48 64.8163 14.12C64.163 13.76 63.6496 13.26 63.2763 12.62C62.903 11.98 62.7163 11.24 62.7163 10.4V4.79996H65.3563V10.34C65.3563 10.74 65.423 11.0866 65.5563 11.38C65.6896 11.66 65.8896 11.8733 66.1563 12.02C66.423 12.1666 66.7363 12.24 67.0963 12.24C67.643 12.24 68.0696 12.0733 68.3763 11.74C68.683 11.4066 68.8363 10.94 68.8363 10.34V4.79996H71.4563V10.4C71.4563 11.2533 71.2696 12 70.8963 12.64C70.5363 13.28 70.023 13.78 69.3563 14.14C68.703 14.4866 67.9496 14.66 67.0963 14.66ZM75.0627 14.46V0.779961H77.6827V14.46H75.0627ZM72.8027 7.09996V4.79996H79.9427V7.09996H72.8027Z"
        fill="currentColor"
      />
    </svg>
  </>
);

export default LandingHeader;
